/**
 * Colours for the per-yard credit pools.
 *
 * Credits are tallied per (driver × yard) — two yards sit at different distances,
 * so their credits are not interchangeable (see migration 210). Colour is how the
 * dispatcher tells the pools apart at a glance, so the SAME yard must be the SAME
 * colour on the driver card, in the ledger, and on the salary panel.
 *
 * The index comes from the backend (`colorIndex` = the yard's stable rank by id),
 * never from the render order of whatever list a component happens to hold —
 * otherwise a driver with only Hayward credits would paint them in Oakland's colour.
 *
 * Hues deliberately avoid the ones already carrying meaning in these views:
 * amber = unsettled credit, emerald = settled, rose/red = error. What is left
 * reads as identity, not status.
 *
 * Tailwind scans source for literal class names, so every class is spelled out —
 * building them by interpolation would compile to nothing.
 */
export interface YardColor {
  /** Value text, e.g. the "+2". */
  text: string
  /** Small filled dot / icon tint. */
  dot: string
  /** Soft chip background + border, for the ledger's yard tag. */
  chip: string
  /** Bar/accent fill, for the balance header. */
  accent: string
}

const PALETTE: YardColor[] = [
  { text: 'text-blue-700',    dot: 'bg-blue-500',    chip: 'bg-blue-50 text-blue-700 border-blue-200',       accent: 'bg-blue-500' },
  { text: 'text-fuchsia-700', dot: 'bg-fuchsia-500', chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200', accent: 'bg-fuchsia-500' },
  { text: 'text-cyan-700',    dot: 'bg-cyan-500',    chip: 'bg-cyan-50 text-cyan-700 border-cyan-200',       accent: 'bg-cyan-500' },
  { text: 'text-violet-700',  dot: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700 border-violet-200', accent: 'bg-violet-500' },
  { text: 'text-lime-700',    dot: 'bg-lime-500',    chip: 'bg-lime-50 text-lime-700 border-lime-200',       accent: 'bg-lime-500' },
  { text: 'text-orange-700',  dot: 'bg-orange-500',  chip: 'bg-orange-50 text-orange-700 border-orange-200', accent: 'bg-orange-500' },
]

/** Entries written before yards were recorded have no pool of their own. */
const UNKNOWN: YardColor = {
  text: 'text-gray-600',
  dot: 'bg-gray-400',
  chip: 'bg-gray-50 text-gray-600 border-gray-200',
  accent: 'bg-gray-400',
}

export function yardColor(colorIndex: number | null | undefined): YardColor {
  if (colorIndex == null || colorIndex < 0) return UNKNOWN
  return PALETTE[colorIndex % PALETTE.length]
}

/** Label for a pool whose yard is missing from the entry. */
export const UNKNOWN_YARD_LABEL = 'No yard'
